import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import BannerComponent from './Banner';
import { useOkta } from '../../hooks/oktaHook';
import CustomStepper from './Stepper/Steps';
import { fetchCalanderExistanceByBU, fetchActualTemplateData } from '../Calendar/service';
import {
  setCalendarExistance,
  setComprehensiveDataRx,
  setStepperTaskRx
} from '../../redux/Calendar/calendarSlice';
import { convertTaskListToStepperTask, isInvalidValue } from '../../utils/Utils';
import AccordionComponent from './AccordionComponent';
import {
  readinessCards,
  cycleTriggerCards,
  alignmentAndAction,
  brandInsightCards,
} from './ReadinessCards';

const HomePage = () => {
  const { userDetails } = useOkta();
  const { state, search } = useLocation();
  const dispatch = useDispatch();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const userRole = userDetails?.roles;
  const email = userDetails?.email;
  const isCalendarCreated = useSelector((state) => state.calendar.calendarExistance);
  const { isExistingUser } = useSelector((state) => state.general);

  const params = new URLSearchParams(search);
  const selectedCycleId = Number(params.get('cycleId'));
  const selectedCycle = params.get('cycleName');
  const selectedBu = params.get('bu');

  const fetchAllRequiredData = useCallback(async () => {
    // if no team / not valud email / not valid bu , skip
    if (isInvalidValue(email) || isInvalidValue(selectedBu || !isExistingUser)) {
      return;
    }

    setIsLoading(true);
    try {
      // 1. Get calendar existence
      const calendarResponse = await fetchCalanderExistanceByBU({
        buName: selectedBu,
        userName: email,
        cycleId: selectedCycleId
      });
      dispatch(setCalendarExistance(calendarResponse));

      // 2. Get comprehensive data if calendar exists
      if (calendarResponse) {
        const payload = {
          buName: selectedBu,
          userEmail: email,
          role: userRole,
          tasksList: [],
          cycleId: selectedCycleId
        };
        const templateResponse = await fetchActualTemplateData(payload);
        dispatch(setComprehensiveDataRx(templateResponse));
        const stepperTasks = convertTaskListToStepperTask(templateResponse);
        setTasks(stepperTasks);
        dispatch(setStepperTaskRx(stepperTasks));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBu, email]);

  useEffect(() => {
    if (!isInvalidValue(selectedBu) && !isInvalidValue(email) && isExistingUser && email !== '') {
      fetchAllRequiredData();
    }
  }, [email, selectedBu, isExistingUser]);

  return (
    <Stack gap="20px" sx={{ padding: '32px 32px 24px', width: '100%' }}>
      <Stack
        sx={{
          justifyContent: 'space-between',
          display: 'flex',
          flexDirection: 'row'
        }}>
        <h3>{state?.cycleName || selectedCycle}</h3>
        <Stack direction={'column'} alignItems="flex-end">
          {/* Adjusted: allows this block to take available space */}
          <Typography variant="extraSmall" fontWeight={400} sx={{ color: '#616161' }}>
            Current BU
          </Typography>
          <Typography variant="extraSmall" fontWeight={700}>
            {selectedBu}
          </Typography>
        </Stack>
      </Stack>
      {/* show calendar banner only if calendar is not existing under that bu */}
      {!isLoading && !isCalendarCreated ? (
        <BannerComponent isCalendarCreated={isCalendarCreated} userRole={userRole} />
      ) : (
        <BannerComponent
          tasks={tasks}
          isLoading={isLoading}
          isCalendarCreated={isCalendarCreated}
        />
      )}
      {isCalendarCreated && <CustomStepper style={{ height: '125px' }} tasks={tasks} />}

      <Stack gap={2}>
        <AccordionComponent
          title="Readiness"
          cards={readinessCards}
          userRole={userRole}
          isCalendarCreated={isCalendarCreated}
        />
        <AccordionComponent
          title="Cycle Analytics"
          cards={[...cycleTriggerCards, ...brandInsightCards]}
          userRole={userRole}
          isCalendarCreated={isCalendarCreated}
        />
        <AccordionComponent
          title="Alignment & Action"
          cards={alignmentAndAction}
          userRole={userRole}
          isCalendarCreated={isCalendarCreated}
        />
      </Stack>
    </Stack>
  );
};

export default HomePage;
